import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingMasterType, isThirdPartyVirtualWarehouse, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingMasterData, WarehousingTransfer, WarehousingTransferReservation } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { requestWarehouseManualReason } from '../warehousing-manual-reason';
import { itemIdentityLabel } from '../warehousing-item-display';

type ScanMode = 'pick' | 'stage' | 'load';

type ReservationRow = {
  itemId: number;
  sourceZoneId?: number;
  sourceConditionId?: number;
  destinationZoneId?: number;
  destinationConditionId?: number;
  reservation: WarehousingTransferReservation;
};

@Component({
  selector: 'app-warehousing-transfer-scan',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, BreadcrumbModule, ButtonModule, IftaLabelModule, InputGroupModule,
    InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule, TagModule, ToastModule,
    PageFormSectionComponent, PageHeroComponent, PageMetricStripComponent, PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-transfer-scan.component.html'
})
export class WarehousingTransferScanComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private messageService = inject(MessageService);

  isLoading = false;
  isScanning = false;
  transfer?: WarehousingTransfer;
  items: WarehousingItem[] = [];
  masterData = masterDataMap();
  scanMode: ScanMode = 'pick';
  scanValue = '';
  selectedReceiptZoneId: number | null = null;
  receiptMode = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Transfer Scan', 'Transfers', '/warehousing/transfers');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.receiptMode = this.route.snapshot.url.some(segment => segment.path === 'receive-scan');
    this.loadTransfer();
  }

  loadTransfer(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      transfer: this.warehousingService.getTransfer(id),
      items: this.warehousingService.getItems(),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.transfer = response.transfer.data;
        this.items = response.items.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.syncScanMode();
        this.syncReceiptZoneSelection();
        this.breadcrumbItems = warehousingBreadcrumb(
          this.transfer?.transferNumber || 'Transfer Scan',
          'Transfers',
          '/warehousing/transfers'
        );
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  setScanMode(mode: ScanMode): void {
    this.scanMode = mode;
  }

  submitScan(): void {
    if (this.isScanning) return;
    const stockUnitCode = (this.scanValue || '').trim();
    if (!this.transfer?.id || !stockUnitCode || !this.supportsStockLedgerWorkflow()) return;
    if (this.receiptMode && !this.canReceive()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Receipt Not Ready',
        detail: this.receiptBlockedMessage()
      });
      return;
    }
    if (this.receiptMode && this.requiresReceiptZoneSelection() && !this.selectedReceiptZoneId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Receipt Zone Required',
        detail: 'Select the destination zone before receiving this stock unit.'
      });
      return;
    }

    this.isScanning = true;
    const request = {
      stockUnitCode,
      zoneId: this.receiptMode ? this.selectedReceiptZoneId : null
    };
    const operation = this.receiptMode
      ? this.warehousingService.scanTransferReceipt(this.transfer.id, request)
      : this.scanMode === 'pick'
        ? this.warehousingService.scanTransferPick(this.transfer.id, request)
        : this.scanMode === 'stage'
          ? this.warehousingService.scanTransferStage(this.transfer.id, request)
          : this.warehousingService.scanTransferLoad(this.transfer.id, request);

    operation.subscribe({
      next: response => {
        this.transfer = response.data;
        this.scanValue = '';
        this.syncScanMode();
        this.syncReceiptZoneSelection();
        this.messageService.add({
          severity: 'success',
          summary: this.receiptMode ? 'Receipt Recorded' : 'Scan Recorded',
          detail: this.receiptMode ? `${stockUnitCode} received at destination.` : `${stockUnitCode} marked as ${this.scanMode}.`
        });
        this.isScanning = false;
      },
      error: () => this.isScanning = false
    });
  }

  confirmDispatch(): void {
    if (!this.transfer?.id || !this.canConfirmDispatch() || this.isScanning) return;
    const reason = requestWarehouseManualReason('Confirm transfer dispatch');
    if (!reason) return;
    this.isScanning = true;
    this.warehousingService.confirmTransferDispatch(this.transfer.id, reason).subscribe({
      next: response => {
        this.transfer = response.data;
        this.syncReceiptZoneSelection();
        this.messageService.add({
          severity: 'success',
          summary: 'Dispatched',
          detail: 'Transfer stock is now in transit and ready for destination receipt scans.'
        });
        this.isScanning = false;
      },
      error: () => this.isScanning = false
    });
  }

  reservationRows(): ReservationRow[] {
    return (this.transfer?.lines || []).flatMap(line => (line.reservations || []).map(reservation => ({
      itemId: line.itemId,
      sourceZoneId: line.sourceZoneId,
      sourceConditionId: line.sourceConditionId,
      destinationZoneId: line.destinationZoneId,
      destinationConditionId: line.destinationConditionId,
      reservation
    })));
  }

  metrics(): PageMetric[] {
    const reservations = this.reservationRows().map(row => row.reservation);
    return [
      { label: 'Reserved Units', value: reservations.length },
      { label: 'Picked', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 1).length, tone: 'info' },
      { label: 'Staged', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 2).length, tone: 'warn' },
      { label: 'Loaded', value: reservations.filter(row => this.scanRank(row.scanStatus) >= 3).length, tone: 'info' },
      { label: 'Received', value: reservations.filter(row => (row.receiptStatus || '').toUpperCase() === 'RECEIVED').length, tone: 'success' }
    ];
  }

  itemName(itemId: number): string {
    const item = this.items.find(row => row.id === itemId);
    return itemIdentityLabel(item);
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return (this.masterData.WAREHOUSE || []).find(item => item.id === id);
  }

  supportsStockLedgerWorkflow(): boolean {
    return !isThirdPartyVirtualWarehouse(this.warehouseById(this.transfer?.sourceWarehouseId))
      && !isThirdPartyVirtualWarehouse(this.warehouseById(this.transfer?.destinationWarehouseId));
  }

  receiptZoneOptions(): WarehousingMasterData[] {
    const destinationWarehouseId = this.transfer?.destinationWarehouseId;
    return destinationWarehouseId
      ? (this.masterData.ZONE || []).filter(item => item.parentId === destinationWarehouseId)
      : [];
  }

  requiresReceiptZoneSelection(): boolean {
    return this.receiptMode && (this.transfer?.lines || []).some(line => !line.destinationZoneId);
  }

  canConfirmDispatch(): boolean {
    return (this.transfer?.status || '').toUpperCase() === 'LOADED';
  }

  canReceive(): boolean {
    return ['IN_TRANSIT', 'PARTIALLY_RECEIVED'].includes((this.transfer?.status || '').toUpperCase());
  }

  receiptBlockedMessage(): string {
    const status = (this.transfer?.status || '').toUpperCase();
    if (status === 'LOADED') {
      return 'Confirm dispatch first so the transfer and stock unit move into transit before destination receipt scans.';
    }
    if (status === 'RECEIVED') {
      return 'This transfer is already fully received.';
    }
    if (status === 'CANCELLED') {
      return 'Cancelled transfers cannot be received.';
    }
    return 'Transfer must be in transit before destination receipt scans.';
  }

  displayDateTime(value?: string): string {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  scanTitle(): string {
    return this.receiptMode ? 'Receive Transfer' : 'Pick / Stage / Load Transfer';
  }

  scanActionLabel(): string {
    return this.receiptMode ? 'Receive Unit' : `Mark ${this.scanMode}`;
  }

  private syncScanMode(): void {
    if (this.receiptMode) return;
    const status = (this.transfer?.status || '').toUpperCase();
    if (status === 'STAGED' || status === 'LOADED') {
      this.scanMode = 'load';
      return;
    }
    if (status === 'PICKED') {
      this.scanMode = 'stage';
      return;
    }
    this.scanMode = 'pick';
  }

  private syncReceiptZoneSelection(): void {
    if (!this.requiresReceiptZoneSelection()) {
      this.selectedReceiptZoneId = null;
      return;
    }
    if (this.selectedReceiptZoneId && this.receiptZoneOptions().some(zone => zone.id === this.selectedReceiptZoneId)) {
      return;
    }
    this.selectedReceiptZoneId = null;
  }

  private scanRank(scanStatus?: string): number {
    switch ((scanStatus || 'PENDING').toUpperCase()) {
      case 'PICKED': return 1;
      case 'STAGED': return 2;
      case 'LOADED': return 3;
      default: return 0;
    }
  }

  statusSeverity = statusSeverity;
}
